import React, { useState, useEffect } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Alert, Stack, Paper } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { format, subMonths, compareAsc } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Import the service
import { fetchTimeBasedReviewAggregation } from '../../../services/reviewsService';

// Import mock data
import { dataPerriod } from '../ReviewsExampleReport';

const TimeBasedReviewChart = ({ exampleReport = false }) => {
  // State variables
  const [aggregationPeriod, setAggregationPeriod] = useState('month');
  const [startDate, setStartDate] = useState(subMonths(new Date(), 12)); // Default to 12 months ago
  const [endDate, setEndDate] = useState(new Date());
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState(null);
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);

  // Get taskId from URL params
  const { taskId } = useParams();

  // Get loading state from Redux
  const isLoading = useSelector((state) => state.loadingReducer.loadingAnswer);

  // Function to process and format chart data
  const processChartData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    // Create display-friendly period labels
    const formattedData = data.map((item) => {
      const periodValue = item.time_period;

      let periodLabel = periodValue;
      if (aggregationPeriod === 'month') {
        // Parse YYYY-MM format
        try {
          // Use a more robust parsing approach
          const [year, month] = periodValue.split('-');
          if (year && month) {
            const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
            periodLabel = format(date, 'MMM yyyy');
          }
        } catch (e) {
          // If parsing fails, keep original
          console.error('Date parsing error:', e);
        }
      } else if (aggregationPeriod === 'week') {
        // Handle week format if needed
        try {
          // Assuming week format is like "2023-W01"
          const parts = periodValue.split('-W');
          if (parts.length === 2) {
            periodLabel = `Week ${parts[1]}, ${parts[0]}`;
          }
        } catch (e) {
          // If parsing fails, keep original
        }
      }

      return {
        ...item,
        period_label: periodLabel
      };
    });

    // Sort by time period
    return formattedData.sort((a, b) => {
      try {
        if (aggregationPeriod === 'month') {
          // More robust sorting
          const [yearA, monthA] = a.time_period.split('-');
          const [yearB, monthB] = b.time_period.split('-');

          if (yearA && monthA && yearB && monthB) {
            const dateA = new Date(parseInt(yearA, 10), parseInt(monthA, 10) - 1, 1);
            const dateB = new Date(parseInt(yearB, 10), parseInt(monthB, 10) - 1, 1);
            return compareAsc(dateA, dateB);
          }
        }
        return a.time_period.localeCompare(b.time_period);
      } catch (e) {
        console.error('Date sorting error:', e);
        return 0;
      }
    });
  };

  // Function to fetch chart data
  const fetchChartData = async () => {
    console.log('fetchChartData called with:', { exampleReport, isRequestInProgress, dataPerriod });

    // Guard against duplicate calls
    if (isRequestInProgress) return;

    setError(null);
    setIsRequestInProgress(true);

    try {
      // Check if we should use mock data
      if (exampleReport) {
        console.log('Using mock data');
        const processedData = processChartData(dataPerriod.data);
        console.log('Processed data:', processedData);
        setChartData(processedData);
        return;
      }

      if (!taskId) {
        setError('Cannot load data: Task ID not available');
        return;
      }

      // Validate and format dates for API
      let formattedStartDate, formattedEndDate;

      try {
        // Ensure we have valid date objects
        const startDateObj = startDate instanceof Date && !isNaN(startDate) ? startDate : new Date(startDate);
        const endDateObj = endDate instanceof Date && !isNaN(endDate) ? endDate : new Date(endDate);

        // Format dates consistently
        formattedStartDate = format(startDateObj, 'yyyy-MM-dd');
        formattedEndDate = format(endDateObj, 'yyyy-MM-dd');

        // Additional validation
        if (!/^\d{4}-\d{2}-\d{2}$/.test(formattedStartDate) || !/^\d{4}-\d{2}-\d{2}$/.test(formattedEndDate)) {
          throw new Error('Invalid date format');
        }
      } catch (err) {
        // Fallback to safe defaults if date parsing fails
        console.error('Date formatting error:', err);
        formattedStartDate = format(subMonths(new Date(), 12), 'yyyy-MM-dd');
        formattedEndDate = format(new Date(), 'yyyy-MM-dd');
      }

      // Fetch data
      const response = await fetchTimeBasedReviewAggregation(taskId, aggregationPeriod, formattedStartDate, formattedEndDate);

      if (response && response.data) {
        const processedData = processChartData(response.data);
        setChartData(processedData);
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error('Chart data fetch error:', err);
      setError('Failed to load chart data. Please try again later.');
      setChartData([]);
    } finally {
      setIsRequestInProgress(false);
    }
  };
  console.log(chartData);
  console.log(isRequestInProgress);
  // Single useEffect to handle all data fetching conditions
  useEffect(() => {
    console.log('useEffect triggered with:', {
      taskId,
      isLoading,
      isRequestInProgress,
      exampleReport
    });
    if ((taskId || exampleReport) && !isLoading && !isRequestInProgress) {
      fetchChartData();
    }
  }, [taskId, isLoading, aggregationPeriod, startDate, endDate, exampleReport]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle period change
  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setAggregationPeriod(newPeriod);
    }
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, minWidth: '180px' }}>
          <Typography variant="subtitle2">{label}</Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="primary.main">
              Average Rating: {payload[0]?.value?.toFixed(1) || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review Count: {payload[1]?.value || 'N/A'}
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  // Don't render while main loading is happening
  if (isLoading) {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        mb: 3,
        overflow: 'hidden',
        borderRadius: '12px'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Rating Trends Over Time
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track how ratings and review volumes have changed over time
          </Typography>
        </Box>
      </Box>

      {/* Controls */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3, mt: 2 }} alignItems="center">
        <ToggleButtonGroup
          value={aggregationPeriod}
          exclusive
          onChange={handlePeriodChange}
          aria-label="time aggregation"
          size="small"
          sx={{ bgcolor: 'background.paper' }}
        >
          <ToggleButton value="week" aria-label="weekly">
            Weekly
          </ToggleButton>
          <ToggleButton value="month" aria-label="monthly">
            Monthly
          </ToggleButton>
        </ToggleButtonGroup>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="500" sx={{ mr: 1 }}>
                Start:
              </Typography>
              <Typography variant="body2" sx={{ mr: 0 }}>
                {format(startDate, 'MMM d, yyyy')}
              </Typography>
              <DatePicker
                value={startDate}
                onChange={(newValue) => {
                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                    setStartDate(newValue);
                  }
                }}
                maxDate={endDate}
                components={{
                  OpenPickerIcon: CalendarTodayIcon
                }}
                renderInput={({ inputRef, InputProps }) => (
                  <Box
                    ref={inputRef}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: 'primary.main',
                      p: 0
                    }}
                  >
                    {InputProps?.endAdornment}
                  </Box>
                )}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="500" sx={{ mr: 1 }}>
                End:
              </Typography>
              <Typography variant="body2" sx={{ mr: 0 }}>
                {format(endDate, 'MMM d, yyyy')}
              </Typography>
              <DatePicker
                value={endDate}
                onChange={(newValue) => {
                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                    setEndDate(newValue);
                  }
                }}
                minDate={startDate}
                maxDate={new Date()}
                components={{
                  OpenPickerIcon: CalendarTodayIcon
                }}
                renderInput={({ inputRef, InputProps }) => (
                  <Box
                    ref={inputRef}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      color: 'primary.main',
                      p: 0
                    }}
                  >
                    {InputProps?.endAdornment}
                  </Box>
                )}
              />
            </Box>
          </Box>
        </LocalizationProvider>
      </Stack>

      {/* Loading indicator and errors */}
      {isRequestInProgress && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading chart data...
        </Alert>
      )}

      {!isRequestInProgress && error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : !isRequestInProgress && chartData.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No data available for the selected time period.
        </Alert>
      ) : (
        !isRequestInProgress && (
          <Box sx={{ height: 400, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="period_label"
                  label={{
                    value: 'Time Period',
                    position: 'insideBottom',
                    offset: -5
                  }}
                />
                <YAxis
                  yAxisId="left"
                  domain={[0, 5]}
                  ticks={[0, 1, 2, 3, 4, 5]}
                  tickFormatter={(value) => value.toFixed(0)}
                  label={{
                    value: 'Average Rating',
                    angle: -90,
                    position: 'insideLeft',
                    style: { textAnchor: 'middle' },
                    offset: 20
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{
                    value: 'Review Count',
                    angle: -90,
                    position: 'insideRight',
                    style: { textAnchor: 'middle' },
                    offset: 20
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                <Line
                  type="monotone"
                  dataKey="average_rating"
                  name="Average Rating"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#2563eb', strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                  yAxisId="left"
                  connectNulls={true}
                />
                <Line
                  type="monotone"
                  dataKey="review_count"
                  name="Review Count"
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  dot={{ r: 3, fill: '#94a3b8', strokeWidth: 0 }}
                  yAxisId="right"
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )
      )}
    </Paper>
  );
};

export default TimeBasedReviewChart;
